import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { LOGO_URL, COMPANY_NAME } from '@/config/brand';
import {
  LayoutDashboard, HardHat, ClipboardList,
  FileText, FolderOpen, Receipt, Users, LogOut, Menu, X, Archive, Calculator, ScanLine, Wallet, CalendarDays, FileInput, Package, Building2, Banknote, FileOutput, Shield, Bell, Truck, BookOpen, ChevronDown, ChevronRight, CalendarClock, Settings, BookMarked, ShieldAlert, GripVertical, Pencil, FolderPlus, RotateCcw, Check, Monitor, History, PieChart, FileSpreadsheet, Sparkles, Wrench, Bug
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { repairPwaAndReload } from '@/lib/pwaRepair';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// =====================================================================
// CATALOGO STATICO DELLE VOCI DI MENÙ
// Ogni voce ha un id stabile usato per il salvataggio del layout custom.
// =====================================================================
const NAV_ITEMS = {
  dashboard: { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  clienti: { path: '/admin/clienti', label: 'Clienti', icon: Building2 },
  fornitori: { path: '/admin/fornitori', label: 'Fornitori', icon: Building2 },
  cantieri: { path: '/admin/cantieri', label: 'Cantieri', icon: HardHat },
  rapportini: { path: '/admin/rapportini', label: 'Rapportini', icon: ClipboardList },
  'import-rapportini': { path: '/admin/import-rapportini', label: 'Importa Rapportini', icon: FileInput },
  presenze: { path: '/admin/presenze', label: 'Presenze', icon: CalendarDays },
  'buste-paga': { path: '/admin/buste-paga', label: 'Buste Paga', icon: Banknote },
  'prima-nota': { path: '/admin/prima-nota', label: 'Prima Nota', icon: BookOpen },
  finanze: { path: '/admin/finanze', label: 'Situazione Finanze', icon: Wallet },
  ddt: { path: '/admin/ddt', label: 'DDT', icon: Truck },
  preventivi: { path: '/admin/preventivi', label: 'Preventivi', icon: Calculator },
  documenti: { path: '/admin/documenti', label: 'Documenti', icon: FolderOpen },
  'materiali-catalogo': { path: '/admin/materiali-catalogo', label: 'Catalogo Materiali', icon: BookMarked },
  scansioni: { path: '/admin/scansioni', label: 'Scansioni Operai', icon: ScanLine },
  soci: { path: '/admin/soci', label: 'Soci e Compensi', icon: Users },
  bilancio: { path: '/admin/bilancio', label: "Bilancio d'Esercizio", icon: PieChart },
  f24: { path: '/admin/f24', label: 'Modello F24', icon: FileSpreadsheet },
  'buste-paga-import': { path: '/admin/buste-paga-import', label: 'Import Buste Paga (AI)', icon: Sparkles },
  archivio: { path: '/admin/archivio', label: 'Archivio', icon: Archive },
  operai: { path: '/admin/operai', label: 'Operai', icon: Users },
  notifiche: { path: '/admin/notifiche', label: 'Notifiche', icon: Bell, badgeKey: 'notifiche' },
  'admin-accounts': { path: '/admin/admin-accounts', label: 'Account Admin', icon: Shield },
  'gestione-admin': { path: '/admin/gestione-admin', label: 'Gestione Admin & Permessi', icon: Shield, permKey: 'gestione_admin' },
  cronologia: { path: '/admin/cronologia', label: 'Cronologia Modifiche', icon: History, auditOnly: true },
  'diagnostica-errori': { path: '/admin/diagnostica-errori', label: 'Diagnostica Errori', icon: Bug },
  magazzino: { path: '/admin/magazzino', label: 'Magazzino', icon: Package },
  // children dei gruppi default
  'fatture-emesse': { path: '/admin/fatturazione/fatture-emesse', label: 'Fatture Emesse (FAT-A)', icon: FileOutput },
  'conti-emessi': { path: '/admin/conti-emessi', label: 'Conti Emessi (da fatturare)', icon: Receipt },
  'note-credito-emesse': { path: '/admin/fatturazione/note-credito-emesse', label: 'Note Credito Emesse (NCR-A)', icon: FileOutput },
  'fatture-ricevute': { path: '/admin/fatturazione/fatture-ricevute', label: 'Fatture Ricevute (FAT-P)', icon: FileInput },
  'note-credito-ricevute': { path: '/admin/fatturazione/note-credito-ricevute', label: 'Note Credito Ricevute (NCR-P)', icon: FileInput },
  scadenziario: { path: '/admin/fatturazione/scadenziario', label: 'Scadenziario', icon: CalendarClock },
  partitario: { path: '/admin/fatturazione/partitario', label: 'Partitario', icon: Users },
  'registri-iva': { path: '/admin/fatturazione/registri-iva', label: 'Registri IVA', icon: BookOpen },
  // iter85: "Fatturazione prevista" rimossa dal menu su richiesta utente (28/07/2026) —
  // resta accessibile dalla pagina DDT (`/admin/ddt` mostra già il widget).
  intestazione: { path: '/admin/impostazioni/intestazione', label: 'Intestazione aziendale', icon: Building2 },
  'modelli-documenti': { path: '/admin/impostazioni/modelli-documenti', label: 'Modelli documenti', icon: FileText },
  'reset-contabilita': { path: '/admin/impostazioni/reset-contabilita', label: 'Reset contabilità', icon: ShieldAlert },
  'modalita-pagamento': { path: '/admin/impostazioni/modalita-pagamento', label: 'Modalità pagamento', icon: Wallet },
  'app-desktop': { path: '/admin/impostazioni/app-desktop', label: 'App Desktop (Windows)', icon: Monitor },
};

const DEFAULT_LAYOUT = [
  { type: 'item', id: 'dashboard' },
  { type: 'item', id: 'clienti' },
  { type: 'item', id: 'fornitori' },
  { type: 'item', id: 'cantieri' },
  { type: 'item', id: 'rapportini' },
  { type: 'item', id: 'import-rapportini' },
  { type: 'item', id: 'presenze' },
  { type: 'item', id: 'buste-paga' },
  { type: 'item', id: 'prima-nota' },
  { type: 'item', id: 'soci' },
  { type: 'item', id: 'f24' },
  { type: 'item', id: 'bilancio' },
  { type: 'item', id: 'buste-paga-import' },
  { type: 'item', id: 'finanze' },
  {
    type: 'group',
    id: 'g-fatturazione',
    label: 'FATTURAZIONE',
    iconKey: 'Receipt',
    children: [
      { type: 'item', id: 'fatture-emesse' },
      { type: 'item', id: 'conti-emessi' },
      { type: 'item', id: 'note-credito-emesse' },
      { type: 'item', id: 'fatture-ricevute' },
      { type: 'item', id: 'note-credito-ricevute' },
      { type: 'item', id: 'scadenziario' },
      { type: 'item', id: 'partitario' },
      { type: 'item', id: 'registri-iva' },
    ],
  },
  { type: 'item', id: 'ddt' },
  { type: 'item', id: 'preventivi' },
  { type: 'item', id: 'documenti' },
  { type: 'item', id: 'materiali-catalogo' },
  { type: 'item', id: 'scansioni' },
  { type: 'item', id: 'archivio' },
  { type: 'item', id: 'operai' },
  { type: 'item', id: 'notifiche' },
  { type: 'item', id: 'admin-accounts' },
  { type: 'item', id: 'gestione-admin' },
  { type: 'item', id: 'cronologia' },
  { type: 'item', id: 'diagnostica-errori' },
  { type: 'item', id: 'magazzino' },
  {
    type: 'group',
    id: 'g-impostazioni',
    label: 'IMPOSTAZIONI',
    iconKey: 'Settings',
    children: [
      { type: 'item', id: 'intestazione' },
      { type: 'item', id: 'modelli-documenti' },
      { type: 'item', id: 'modalita-pagamento' },
      { type: 'item', id: 'app-desktop' },
      { type: 'item', id: 'reset-contabilita' },
    ],
  },
];

const GROUP_ICONS = { Receipt, Settings, FolderOpen, FolderPlus, Package, BookOpen, Wallet };

// =====================================================================
// LAYOUT NORMALIZER: rimuove duplicati, aggiunge item nuovi mai visti
// =====================================================================
function normalizeLayout(saved) {
  const layout = Array.isArray(saved) && saved.length
    ? JSON.parse(JSON.stringify(saved))
    : JSON.parse(JSON.stringify(DEFAULT_LAYOUT));
  const seen = new Set();
  const collect = (n) => {
    if (!n) return;
    if (n.type === 'item') seen.add(n.id);
    else if (n.type === 'group' && Array.isArray(n.children)) n.children.forEach(collect);
  };
  layout.forEach(collect);
  Object.keys(NAV_ITEMS).forEach(id => {
    if (!seen.has(id) && NAV_ITEMS[id].path) {
      layout.push({ type: 'item', id });
    }
  });
  return layout;
}

// =====================================================================
// SortableItem — solo per le voci foglia (type: 'item') in qualsiasi posizione
// =====================================================================
function SortableItem({ dndId, itemId, isChild, isEdit, location, unreadCount, onMobileNav }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: dndId });
  const { user: navUser } = useAuth();
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const meta = NAV_ITEMS[itemId];
  if (!meta) return null;
  if (meta.auditOnly && (navUser?.email || '').trim().toLowerCase() !== 'f.conti@crimpiantielettrici.it') return null;
  const Icon = meta.icon;
  const isActive = location.pathname === meta.path;
  return (
    <div ref={setNodeRef} style={style} className={isEdit ? 'flex items-center gap-1 px-1 py-0.5 bg-amber-50/50 border border-dashed border-amber-300 rounded-md' : ''}>
      {isEdit && (
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-slate-400 hover:text-amber-600" aria-label="Trascina voce">
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      {isEdit ? (
        <div className={`flex-1 flex items-center px-3 rounded-md font-medium text-slate-600 ${isChild ? 'gap-2.5 py-1.5 text-xs' : 'gap-3 py-2 text-sm'}`}>
          <Icon className={`flex-shrink-0 ${isChild ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} />
          <span className="flex-1">{meta.label}</span>
        </div>
      ) : (
        <Link
          to={meta.path}
          onClick={onMobileNav}
          className={`flex items-center px-3 rounded-md font-medium transition-all duration-150 flex-1 ${isChild ? 'gap-2.5 py-1.5 text-xs' : 'gap-3 py-2 text-sm'} ${isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
          data-testid={`nav-${itemId}`}
        >
          <Icon className={`flex-shrink-0 ${isChild ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} />
          <span className="flex-1">{meta.label}</span>
          {meta.badgeKey === 'notifiche' && unreadCount > 0 && (
            <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold rounded-full bg-red-500 text-white" data-testid="admin-nav-unread-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Link>
      )}
    </div>
  );
}

// =====================================================================
// SortableGroup — header del gruppo (draggable) + lista children
// =====================================================================
function SortableGroup({ group, isEdit, location, unreadCount, onMobileNav, onRenameGroup, onDeleteGroup, openGroups, toggleGroup }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: group.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const Icon = GROUP_ICONS[group.iconKey] || FolderOpen;
  const isOpen = openGroups[group.id] === true;
  const isActive = (group.children || []).some(c => NAV_ITEMS[c.id]?.path === location.pathname);
  const isProtected = group.id === 'g-fatturazione' || group.id === 'g-impostazioni';
  return (
    <div ref={setNodeRef} style={style}>
      <div className={`flex items-center gap-1 px-1 py-0.5 ${isEdit ? 'bg-purple-50/50 border border-dashed border-purple-300 rounded-md' : ''}`}>
        {isEdit && (
          <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-slate-400 hover:text-purple-600" aria-label="Trascina gruppo">
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => toggleGroup(group.id)}
          className={`flex-1 flex items-center gap-3 px-2 py-2 rounded-md text-sm font-bold uppercase tracking-wide transition-all duration-150 ${isActive ? 'text-slate-900 bg-slate-100' : 'text-slate-700 hover:bg-slate-100'}`}
          data-testid={`nav-group-${group.id}`}
        >
          <Icon className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1 text-left truncate">{group.label}</span>
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {isEdit && (
          <>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onRenameGroup(group)} title="Rinomina"><Pencil className="h-3 w-3" /></Button>
            {!isProtected && (
              <Button variant="ghost" size="icon" className="h-6 w-6 text-red-600" onClick={() => onDeleteGroup(group)} title="Elimina gruppo">
                <X className="h-3 w-3" />
              </Button>
            )}
          </>
        )}
      </div>
      {isOpen && (
        <div className="mt-0.5 ml-3 pl-2 border-l border-slate-200 space-y-0.5">
          <SortableContext items={(group.children || []).map(c => `${group.id}__${c.id}`)} strategy={verticalListSortingStrategy}>
            {(group.children || []).map(child => (
              <SortableItem
                key={`${group.id}__${child.id}`}
                dndId={`${group.id}__${child.id}`}
                itemId={child.id}
                isChild
                isEdit={isEdit}
                location={location}
                unreadCount={unreadCount}
                onMobileNav={onMobileNav}
              />
            ))}
          </SortableContext>
        </div>
      )}
    </div>
  );
}

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // iter101: modalità "embedded" — quando la pagina è caricata dentro una finestra
  // del workspace multi-finestra (?embedded=1), nascondi sidebar/header e mostra
  // solo il contenuto della sezione.
  const isEmbedded = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('embedded') === '1';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [openGroups, setOpenGroups] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [savingLayout, setSavingLayout] = useState(false);
  // Dialog state for "Nuovo gruppo" / "Rinomina gruppo"
  const [groupDialog, setGroupDialog] = useState({ open: false, mode: 'create', target: null, name: '' });

  // iter59: helper permessi. Mappa menu-item id → sezione permessi. Se un item non è
  // mappato qui, resta sempre visibile (voci non permessate).
  const ITEM_TO_SECTION = {
    dashboard: 'dashboard', clienti: 'clienti', fornitori: 'fornitori', cantieri: 'cantieri',
    rapportini: 'rapportini', 'import-rapportini': 'importa_rapportini',
    presenze: 'presenze', 'buste-paga': 'buste_paga', contabilita: 'contabilita',
    'prima-nota': 'prima_nota', finanze: 'situazione_finanze', ddt: 'ddt',
    preventivi: 'preventivi', documenti: 'documenti',
    'fatture-emesse': 'fatture_emesse', 'conti-emessi': 'conti_emessi',
    'note-credito-emesse': 'note_credito_emesse',
    'fatture-ricevute': 'fatture_ricevute', 'note-credito-ricevute': 'note_credito_ricevute',
    'gestione-admin': 'gestione_admin',
  };
  const isSuper = !!user?.is_super_admin;
  const isMagazziniereOnly = user?.role === 'magazziniere' || (user?.is_magazziniere === true && user?.role === 'operaio');
  const isMagazziniere = user?.role === 'magazziniere' || user?.is_magazziniere === true;
  const canViewItem = (itemId) => {
    // Magazziniere puro o operaio flaggato: vede SOLO la voce Magazzino
    if (isMagazziniereOnly) return itemId === 'magazzino';
    if (isSuper) return true;
    const section = ITEM_TO_SECTION[itemId];
    if (!section) return true;  // voci non permessate → visibili
    const p = user?.permissions?.[section];
    if (!p) return true;  // legacy admin senza permessi salvati
    return !!p.view;
  };

  // Filtra il layout: rimuove voci non visibili e gruppi vuoti
  const visibleLayout = layout
    .map(n => {
      if (n.type === 'group') {
        const kids = (n.children || []).filter(c => canViewItem(c.id));
        if (kids.length === 0) return null;
        return { ...n, children: kids };
      }
      return canViewItem(n.id) ? n : null;
    })
    .filter(Boolean);

  // -------------- Load layout from backend --------------
  useEffect(() => {
    const fetchLayout = async () => {
      try {
        const r = await axios.get(`${API}/admin/nav-layout`, { withCredentials: true });
        const saved = r.data?.layout;
        setLayout(normalizeLayout(saved));
      } catch {
        setLayout(DEFAULT_LAYOUT);
      }
    };
    fetchLayout();
  }, []);

  // Auto-open dei gruppi che contengono la rotta corrente (chiusi di default)
  useEffect(() => {
    const next = {};
    layout.forEach(n => {
      if (n.type === 'group' && (n.children || []).some(c => NAV_ITEMS[c.id]?.path === location.pathname)) {
        next[n.id] = true;
      }
    });
    setOpenGroups(prev => ({ ...prev, ...next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, location.pathname]);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API}/notifications`, { withCredentials: true });
        setUnreadCount((res.data || []).filter(n => !n.read).length);
      } catch (err) {
        console.warn('Errore aggiornamento notifiche non lette:', err?.message);
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // iter90: Ripara app (pulizia cache PWA + service worker + hard reload)
  const [repairOpen, setRepairOpen] = useState(false);
  const [repairing, setRepairing] = useState(false);
  // iter95: Segnalazione manuale schermata anomala (arriva in Diagnostica anche se boundary non registra)
  const [issueType, setIssueType] = useState('blue_screen');
  const [issueDescription, setIssueDescription] = useState('');
  const handleRepairApp = async () => {
    setRepairing(true);
    try {
      // Prima invia la segnalazione (best-effort, non blocca)
      try {
        await fetch(`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/client-errors/report-visual`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            issue_type: issueType,
            description: issueDescription,
            location: window.location.href,
            user_agent: navigator.userAgent,
          }),
        });
      } catch (_) { /* silent */ }
      // Poi pulisci e ricarica
      await new Promise((r) => setTimeout(r, 300));
      await repairPwaAndReload();
    } catch (_) {
      setRepairing(false);
      window.location.reload();
    }
  };

  // -------------- DnD --------------
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  // Flatten layout to dnd-ids: top-level item/group → `id`; child item → `groupId__itemId`
  const flatDndIds = useMemo(() => {
    const ids = [];
    layout.forEach(n => {
      ids.push(n.id);
      if (n.type === 'group') (n.children || []).forEach(c => ids.push(`${n.id}__${c.id}`));
    });
    return ids;
  }, [layout]);

  const findContainer = (dndId) => {
    if (!dndId.includes('__')) {
      // top-level group or item
      const idx = layout.findIndex(n => n.id === dndId);
      return { level: 'top', index: idx };
    }
    const [groupId, itemId] = dndId.split('__');
    const groupIdx = layout.findIndex(n => n.id === groupId);
    if (groupIdx < 0) return null;
    const childIdx = (layout[groupIdx].children || []).findIndex(c => c.id === itemId);
    return { level: 'child', groupIdx, childIdx };
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = findContainer(active.id);
    const to = findContainer(over.id);
    if (!from || !to) return;

    setLayout(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      // Case 1: both top-level → simple arrayMove
      if (from.level === 'top' && to.level === 'top') {
        return arrayMove(next, from.index, to.index);
      }
      // Case 2: child → child (same group)
      if (from.level === 'child' && to.level === 'child' && from.groupIdx === to.groupIdx) {
        next[from.groupIdx].children = arrayMove(next[from.groupIdx].children, from.childIdx, to.childIdx);
        return next;
      }
      // Case 3: child → top-level (extract from group)
      if (from.level === 'child' && to.level === 'top') {
        const [moved] = next[from.groupIdx].children.splice(from.childIdx, 1);
        // adjust to.index if removing earlier shifted positions
        const targetIdx = to.index;
        next.splice(targetIdx, 0, moved);
        return next;
      }
      // Case 4: top-level item → child (move into group)
      if (from.level === 'top' && to.level === 'child') {
        const [moved] = next.splice(from.index, 1);
        if (moved.type !== 'item') {
          // gruppi non si annidano: rollback
          next.splice(from.index, 0, moved);
          return next;
        }
        // groupIdx might shift after splice
        const adjGroupIdx = to.groupIdx > from.index ? to.groupIdx - 1 : to.groupIdx;
        next[adjGroupIdx].children.splice(to.childIdx, 0, moved);
        return next;
      }
      // Case 5: child of group A → child of group B
      if (from.level === 'child' && to.level === 'child' && from.groupIdx !== to.groupIdx) {
        const [moved] = next[from.groupIdx].children.splice(from.childIdx, 1);
        next[to.groupIdx].children.splice(to.childIdx, 0, moved);
        return next;
      }
      return prev;
    });
  };

  // -------------- Save / reset --------------
  const saveLayout = async () => {
    setSavingLayout(true);
    try {
      await axios.put(`${API}/admin/nav-layout`, { layout }, { withCredentials: true });
      toast.success('Menù salvato');
      setIsEdit(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio menù');
    } finally {
      setSavingLayout(false);
    }
  };
  const resetLayout = async () => {
    if (!window.confirm('Ripristinare il menù di default?')) return;
    try {
      await axios.delete(`${API}/admin/nav-layout`, { withCredentials: true });
      setLayout(DEFAULT_LAYOUT);
      toast.success('Menù ripristinato');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore reset');
    }
  };

  // -------------- Group management --------------
  const openCreateGroupDialog = () => setGroupDialog({ open: true, mode: 'create', target: null, name: '' });
  const openRenameGroupDialog = (group) => setGroupDialog({ open: true, mode: 'rename', target: group, name: group.label });
  const closeGroupDialog = () => setGroupDialog({ open: false, mode: 'create', target: null, name: '' });

  const confirmGroupDialog = () => {
    const label = (groupDialog.name || '').trim();
    if (!label) { toast.error('Inserisci un nome per il gruppo'); return; }
    if (groupDialog.mode === 'create') {
      setLayout(prev => [...prev, {
        type: 'group',
        id: `g-custom-${Date.now()}`,
        label: label.toUpperCase(),
        iconKey: 'FolderPlus',
        children: [],
      }]);
      toast.success('Gruppo creato — trascinaci dentro le voci');
    } else if (groupDialog.target) {
      setLayout(prev => prev.map(n => n.id === groupDialog.target.id ? { ...n, label: label.toUpperCase() } : n));
    }
    closeGroupDialog();
  };

  const deleteGroup = (group) => {
    if (!window.confirm(`Eliminare il gruppo "${group.label}"? Le voci verranno spostate fuori dal gruppo.`)) return;
    setLayout(prev => {
      const next = [];
      prev.forEach(n => {
        if (n.id === group.id) {
          (n.children || []).forEach(c => next.push(c));
        } else {
          next.push(n);
        }
      });
      return next;
    });
  };

  const toggleGroup = useCallback((id) => {
    setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // iter101: in embedded mode mostra solo il contenuto (nessuna sidebar/header)
  if (isEmbedded) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="p-4 lg:p-6">{children}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <div className="lg:hidden flex items-center justify-between px-4 pb-3 pt-[max(env(safe-area-inset-top),0.75rem)] bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="CR" className="h-8 w-auto object-contain bg-white rounded p-0.5" />
          <span className="font-bold text-slate-900 text-sm">{COMPANY_NAME}</span>
        </div>
        <div className="flex items-center gap-1">
          <Link to="/admin/notifiche" className="relative inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-slate-100" data-testid="mobile-bell-btn">
            <Bell className="h-5 w-5 text-slate-700" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[9px] font-bold rounded-full bg-red-500 text-white" data-testid="mobile-bell-badge">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Link>
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-btn">
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center gap-3 p-5 border-b border-slate-200">
            <div className="bg-white rounded-lg p-1.5 flex-shrink-0">
              <img src={LOGO_URL} alt="CR" className="h-10 w-auto object-contain" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="font-bold text-slate-900 block text-sm leading-tight truncate">{COMPANY_NAME}</span>
              <span className="text-[10px] text-slate-400 uppercase tracking-wider">Gestionale</span>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setIsEdit(v => !v)} title={isEdit ? 'Esci modalità modifica' : 'Modifica menù'} data-testid="toggle-edit-menu" style={{ display: isMagazziniereOnly ? 'none' : undefined }}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Edit toolbar */}
          {isEdit && (
            <div className="px-3 py-2 bg-purple-50 border-b border-purple-200 flex flex-wrap gap-1.5">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openCreateGroupDialog} data-testid="add-group"><FolderPlus className="h-3 w-3 mr-1" />Nuovo gruppo</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={resetLayout} data-testid="reset-layout"><RotateCcw className="h-3 w-3 mr-1" />Default</Button>
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white ml-auto" onClick={saveLayout} disabled={savingLayout} data-testid="save-layout">
                <Check className="h-3 w-3 mr-1" />{savingLayout ? 'Salvo…' : 'Salva'}
              </Button>
            </div>
          )}

          <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={flatDndIds} strategy={verticalListSortingStrategy}>
                {visibleLayout.map(node => (
                  node.type === 'group' ? (
                    <SortableGroup
                      key={node.id}
                      group={node}
                      isEdit={isEdit}
                      location={location}
                      unreadCount={unreadCount}
                      onMobileNav={() => setSidebarOpen(false)}
                      onRenameGroup={openRenameGroupDialog}
                      onDeleteGroup={deleteGroup}
                      openGroups={openGroups}
                      toggleGroup={toggleGroup}
                    />
                  ) : (
                    <SortableItem
                      key={node.id}
                      dndId={node.id}
                      itemId={node.id}
                      isEdit={isEdit}
                      location={location}
                      unreadCount={unreadCount}
                      onMobileNav={() => setSidebarOpen(false)}
                    />
                  )
                ))}
              </SortableContext>
            </DndContext>
            {isEdit && (
              <p className="text-[10px] text-slate-400 italic mt-3 px-2">
                Trascina con il pallino a sinistra. Voci dei gruppi → fuori per renderle top-level; voci top-level → dentro un gruppo per raggruppare.
              </p>
            )}
          </nav>

          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-sm font-bold text-blue-700">{user?.name?.charAt(0)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" />
              Esci
            </Button>
            {isMagazziniereOnly && (
              <Button
                variant="outline"
                className="w-full mt-2 justify-start border-blue-200 text-blue-700 hover:bg-blue-50"
                onClick={() => navigate('/worker')}
                data-testid="back-to-worker-btn"
                title="Torna alla dashboard operaio (rapportini, ferie, ecc.)"
              >
                <HardHat className="h-4 w-4 mr-2" />
                Dashboard operaio
              </Button>
            )}
            {/* iter90: Ripara app (pulizia cache PWA senza dover usare DevTools) */}
            <Button
              variant="outline"
              className="w-full mt-2 justify-start border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
              onClick={() => setRepairOpen(true)}
              data-testid="repair-app-btn"
              title="Pulisce la cache dell'app e ricarica. Usa questo se vedi errori strani o pagine bianche/blu."
            >
              <Wrench className="h-4 w-4 mr-2" />
              Ripara app
            </Button>
          </div>
        </div>
      </aside>

      {/* iter90: Dialog conferma "Ripara app" */}
      <Dialog open={repairOpen} onOpenChange={(v) => { if (!repairing) setRepairOpen(v); }}>
        <DialogContent className="sm:max-w-md" data-testid="repair-app-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-amber-600" />
              Ripara app
            </DialogTitle>
            <DialogDescription className="pt-2">
              Se vedi errori strani, pagine bianche o blu, o vecchie versioni dell&apos;app, premi <b>Ripara ora</b>. L&apos;app pulirà la cache locale e la ricaricherà.
              <br /><br />
              <span className="text-emerald-700 font-medium">I tuoi dati sono al sicuro nel database.</span>
              <br /><br />
              <span className="text-slate-600 text-[12px] block bg-amber-50 border border-amber-200 rounded-md p-2">
                <b>⚠️ Se il problema torna subito dopo il repair:</b> è probabilmente causato da <b>Google Traduttore</b> o da un&apos;estensione del browser (Grammarly, LanguageTool, Adblock). Prova a:
                <br />1. Disattivare la <b>traduzione automatica</b> della pagina (icona in alto a destra della barra URL)
                <br />2. Aprire l&apos;app in <b>finestra anonima / incognito</b>
                <br />3. Disattivare estensioni sospette
              </span>
            </DialogDescription>
          </DialogHeader>

          {/* iter95: mini-form segnalazione */}
          <div className="space-y-3 py-2 border-t border-slate-100 mt-1">
            <p className="text-xs font-medium text-slate-700">📋 Aiutami a capire il problema (opzionale ma utile):</p>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Cosa vedi?</Label>
              <select
                value={issueType}
                onChange={(e) => setIssueType(e.target.value)}
                disabled={repairing}
                className="w-full h-9 border border-slate-200 rounded-md px-3 text-sm bg-white"
                data-testid="repair-issue-type"
              >
                <option value="blue_screen">Schermata tutta blu / nera</option>
                <option value="white_screen">Schermata bianca vuota</option>
                <option value="partial_broken">Un pezzo di pagina non funziona</option>
                <option value="frozen">Pagina bloccata / non risponde</option>
                <option value="other">Altro</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Descrizione (opzionale)</Label>
              <textarea
                value={issueDescription}
                onChange={(e) => setIssueDescription(e.target.value)}
                disabled={repairing}
                rows={2}
                placeholder="Es: dopo aver cliccato 'Fatture ricevute'..."
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-y"
                data-testid="repair-issue-description"
              />
              <p className="text-[10px] text-slate-500">La segnalazione arriva all&apos;admin in <b>Diagnostica Errori</b> anche se l&apos;app è bloccata.</p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setRepairOpen(false)}
              disabled={repairing}
              data-testid="repair-app-cancel"
            >
              Annulla
            </Button>
            <Button
              type="button"
              className="bg-amber-600 text-white hover:bg-amber-700"
              onClick={handleRepairApp}
              disabled={repairing}
              data-testid="repair-app-confirm"
            >
              <Wrench className="h-4 w-4 mr-2" />
              {repairing ? 'Riparazione in corso...' : 'Segnala e ripara'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="lg:ml-64 min-h-screen pb-[env(safe-area-inset-bottom)]">
        <div className="p-4 lg:p-8">{children}</div>
      </main>

      {/* Dialog Nuovo / Rinomina gruppo */}
      <Dialog open={groupDialog.open} onOpenChange={(o) => { if (!o) closeGroupDialog(); }}>
        <DialogContent className="max-w-sm" data-testid="group-dialog">
          <DialogHeader>
            <DialogTitle>{groupDialog.mode === 'create' ? 'Nuovo gruppo menù' : 'Rinomina gruppo'}</DialogTitle>
            <DialogDescription>
              {groupDialog.mode === 'create'
                ? 'Crea un gruppo personalizzato per organizzare le voci della sidebar. Trascinaci dentro le voci dopo averlo creato.'
                : 'Aggiorna il nome del gruppo. Il nome viene mostrato in maiuscolo.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="group-name">Nome gruppo</Label>
            <Input
              id="group-name"
              value={groupDialog.name}
              onChange={(e) => setGroupDialog(prev => ({ ...prev, name: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmGroupDialog(); } }}
              placeholder="es. FORNITORI E ACQUISTI"
              autoFocus
              data-testid="group-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeGroupDialog} data-testid="group-cancel">Annulla</Button>
            <Button onClick={confirmGroupDialog} className="bg-slate-900 text-white" data-testid="group-confirm">
              {groupDialog.mode === 'create' ? 'Crea' : 'Rinomina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
